import React, { useState } from 'react';
import { TextField, Button, MenuItem, FormControl, InputLabel, Select, Typography, Grid, CircularProgress, InputAdornment, IconButton } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { Auth } from 'aws-amplify';
import NeokodeButton from '../components/NeokodeButton';
import { Link as RouterLink } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import { makeStyles } from '@material-ui/core/styles';
import user_access from '../data/user_access';
import { invokeApig } from '../lib/awsLib';
import { validateEmail, validateName } from '../lib/validate';

const useStyles = makeStyles((theme) => ({
  formControl: {
    //margin: theme.spacing(1),
    minWidth: 120,
  },
  button: {
    margin: theme.spacing(1),
  },
  title: {
    margin: theme.spacing(1),
    textAlign: 'center',
  },
  paper: {
    padding: theme.spacing(2),
    margin: theme.spacing(2, 0),
  },
  accessTitle: {
    marginTop: '40px',
  },
  buttonGoBack: {
    marginTop: '15px',
  },
}));

function UserForm(props) {
  const classes = useStyles();
  const readOnly = props.params.currentUser && props.user && props.params.currentUser.attributes.email === props.user.email;
  const access = props.params.access;
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [message, setMessage] = useState('');
  const [user, setUser] = useState({
    email: props.user ? props.user.email : '',
    name: props.user ? props.user.name : '',
    lastName: props.user ? props.user.lastName : '',
    phoneNumber: props.user ? props.user.phoneNumber : '',
    status: props.user ? props.user.status : '',
    user_access: props.user ? props.user.user_access : {},
  });
  // Flujo de creación en dos pasos: primero se busca el correo y según el
  // resultado se ramifica: 'create' (no existe), 'invite' (existe en otra
  // empresa) o 'member' (ya pertenece a esta empresa, se edita directo)
  const [lookupMode, setLookupMode] = useState(null);
  const [searchEmail, setSearchEmail] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const createUser = (user) => {
    return invokeApig({
      path: "/user",
      method: "POST",
      body: user
    });
  };

  const updateUser = (user) => {
    return invokeApig({
      path: `/user`,
      method: "PUT",
      body: user
    });
  };

  const lookupUser = (email) => {
    return invokeApig({
      path: "/user/lookup",
      method: "POST",
      body: { email }
    });
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    const email = searchEmail.trim().toLowerCase();
    if (!validateEmail(email)) {
      setMessage('Debes ingresar el email correctamente');
      setIsError(true);
      return;
    }
    try {
      setIsError(false);
      setIsSearching(true);
      const currentUser = await Auth.currentAuthenticatedUser();
      if (currentUser.attributes.email === email) {
        setMessage('No puedes modificar tu propia cuenta desde esta opción. Usa Mi perfil.');
        setIsError(true);
        setIsSearching(false);
        return;
      }
      const response = await lookupUser(email);
      if (response.code !== 0) {
        setMessage(response.message || 'Ocurrió un error al buscar el usuario, intenta nuevamente');
        setIsError(true);
        setIsSearching(false);
        return;
      }
      if (!response.exists) {
        setUser({ email: email, name: '', lastName: '', phoneNumber: '', status: '', user_access: {} });
        setLookupMode('create');
      } else {
        setUser({
          email: response.data.email,
          name: response.data.name || '',
          lastName: response.data.lastName || '',
          phoneNumber: response.data.phoneNumber || '',
          status: response.data.status || '',
          user_access: response.inCompany ? (response.data.user_access || {}) : {},
        });
        setLookupMode(response.inCompany ? 'member' : 'invite');
      }
    } catch (error) {
      console.error('Error al buscar el usuario:', error);
      setMessage('Ocurrió un error al buscar el usuario, intenta nuevamente');
      setIsError(true);
    }
    setIsSearching(false);
  };

  const handleSearchAgain = () => {
    setLookupMode(null);
    setSearchEmail('');
    setIsError(false);
    setMessage('');
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setUser({ ...user, [name]: value });
  };

  const handleChangeAccess = (event) => {
    const { name, value } = event.target;
    const attr = name.split('_');
    const user_access = { ...user.user_access, [attr[1]]: [value] };
    setUser({ ...user, user_access: user_access });
  }

  const validate = () => {
    let message = undefined;
    if (!validateEmail(user.email)) {
      message = 'Debes ingresar el email correctamente';
    } else {
      if (!validateName(user.name)) {
        message = 'Debes ingresar el nombre correctamente';
      } else {
        if (!validateName(user.lastName)) {
          message = 'Debes ingresar el apellido correctamente';
        }
      }
    }
    return message;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    const errorMsg = validate();
    if (errorMsg) {
      setMessage(errorMsg);
      setIsError(true);
      setIsLoading(false);
      return;
    }
    const new_user = {
      email: user.email,
      name: user.name,
      lastName: user.lastName,
      phoneNumber: !!user.phoneNumber ? user.phoneNumber : '',
      status: user.status,
      user_access: user.user_access
    };
    if (props.user || lookupMode === 'member') { //editar (ruta de edición o usuario que ya pertenece a la empresa)
      const response = await updateUser(new_user);
      if (response.errorMessage) {
        let msg = 'Error al modificar el usuario, por favor revisa los datos e intenta nuevamente';
        setMessage(msg);
        setIsError(true);
      } else {
        setMessage('El usuario se ha modificado correctamente');
        setIsSuccess(true);
      }
    } else { //crear
      const response = await createUser(new_user);
      if (response.errorMessage) {
        let msg = 'Error al crear el usuario, por favor revisa los datos e intenta nuevamente';
        if (response.errorType === 'UsernameExistsException') {
          msg = 'El usuario ya existe. Puedes eliminarlo y crearlo nuevamente o resetear la contraseña.'
        }
        setMessage(msg);
        setIsError(true);
      } else if (response.code === 1) {
        setMessage('El usuario ya existía en la plataforma y fue agregado a esta empresa con los permisos indicados. Podrá acceder con su contraseña actual.');
        setIsSuccess(true);
      } else if (response.code === 2) {
        setMessage('El usuario ya pertenece a esta empresa. Si quieres cambiar sus permisos, edítalo desde la lista de usuarios.');
        setIsError(true);
      } else if (response.code === 0) {
        setMessage('El usuario se ha creado correctamente');
        setIsSuccess(true);
      } else {
        setMessage(response.message || 'Error al crear el usuario, por favor revisa los datos e intenta nuevamente');
        setIsError(true);
      }
    }
    setIsLoading(false);
  };

  const handleGoBack = (event) => {
    event.preventDefault();
    if (props.onFinish) {
      props.onFinish(false);
    } else {
      props.history.push('/users');
    }
  }

  if (isSuccess) {
    return (
      <>
        <Alert severity="success">{message}</Alert>
        <NeokodeButton variant="contained" component={RouterLink} to="/users" className={classes.buttonGoBack}>Volver</NeokodeButton>
      </>
    );
  }

  //paso 1 del flujo de creación: buscar el correo antes de mostrar el formulario
  if (!props.user && lookupMode === null) {
    return (
      <>
        <form onSubmit={handleSearch}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="info">
                Ingresa el correo del usuario. Si ya existe en la plataforma cargaremos sus datos automáticamente para que no tengas que escribirlos de nuevo.
              </Alert>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                name="searchEmail"
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                required
                fullWidth
                autoFocus
                disabled={isSearching}
                className={classes.formControl}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton type="submit" aria-label="buscar usuario" disabled={isSearching}>
                        {isSearching ? <CircularProgress size={24} /> : <SearchIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
          {
            isError &&
            <Alert severity="error">
              {message}
            </Alert>
          }
          <Grid container justifyContent="flex-end">
            <Grid item>
              <Button
                type="button"
                variant="contained"
                className={classes.button}
                onClick={handleGoBack}
                disabled={isSearching}
              >
                Volver
              </Button>
            </Grid>
            <Grid item>
              <NeokodeButton
                type="submit"
                variant="contained"
                className={classes.button}
                disabled={isSearching}
              >
                {
                  isSearching ?
                  <CircularProgress />
                  :
                  'Buscar'
                }
              </NeokodeButton>
            </Grid>
          </Grid>
        </form>
      </>
    );
  }

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          {
            lookupMode === 'create' &&
            <Grid item xs={12}>
              <Alert severity="info">El correo no está registrado en la plataforma. Completa los datos para crear el usuario.</Alert>
            </Grid>
          }
          {
            lookupMode === 'invite' &&
            <Grid item xs={12}>
              <Alert severity="info">El usuario ya existe en la plataforma y cargamos sus datos. Configura sus permisos para agregarlo a esta empresa; mantendrá su contraseña actual.</Alert>
            </Grid>
          }
          {
            lookupMode === 'member' &&
            <Grid item xs={12}>
              <Alert severity="info">El usuario ya pertenece a esta empresa. Puedes modificar sus datos y permisos directamente.</Alert>
            </Grid>
          }
          <Grid item xs={12} sm={6}>
            <TextField label="Nombre" name="name" value={user.name} onChange={handleChange} required fullWidth className={classes.formControl} InputProps={{ readOnly: readOnly || lookupMode === 'invite', }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Apellido" name="lastName" value={user.lastName} onChange={handleChange} required fullWidth className={classes.formControl} InputProps={{ readOnly: readOnly || lookupMode === 'invite', }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Email" name="email" value={user.email} onChange={handleChange} required fullWidth className={classes.formControl} InputProps={{ readOnly: readOnly || !props.user, }} />
          </Grid>
          {
            !props.user &&
            <Grid item xs={12} sm={6}>
              <Button type="button" variant="outlined" className={classes.button} onClick={handleSearchAgain} disabled={isLoading}>
                Buscar otro correo
              </Button>
            </Grid>
          }
          {
            (!!props.user || lookupMode === 'member') &&
            <>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth className={classes.formControl}>
                  <InputLabel>Estado</InputLabel>
                  <Select name='status' value={user.status} onChange={handleChange} inputProps={{ readOnly: readOnly, }}>
                    <MenuItem value="Activo">Activo</MenuItem>
                    <MenuItem value="Inactivo">Inactivo</MenuItem>
                    <MenuItem value="Debe cambiar clave" disabled={true}>Debe cambiar clave</MenuItem>
                    <MenuItem value="Con problemas" disabled={true}>Con problemas</MenuItem>
                    <MenuItem value="Sin confirmar" disabled={true}>Sin confirmar</MenuItem>
                    <MenuItem value="Deshabilitado" disabled={true}>Deshabilitado</MenuItem>
                    <MenuItem value="Desconocido" disabled={true}>Desconocido</MenuItem>
                  </Select>
                </FormControl>
             </Grid>
            </>
          }
        </Grid>
        <Typography variant="h6" className={classes.accessTitle}>Permisos</Typography>
        <Grid container spacing={2}>
          {
            Object.keys(user_access).map((key, index) => {
              const app = access[key];
              if (app && app.length > 0) {
                const access_obj = user_access[key];
                return (
                  <Grid item xs={12} sm={6} key={index}>
                    <FormControl fullWidth className={classes.formControl}>
                      <InputLabel>{access_obj.name}</InputLabel>
                      <Select name={`access_${key}`} value={!!user && user.user_access[key] ? user.user_access[key] : ''} onChange={handleChangeAccess} inputProps={{ readOnly: readOnly, }}>
                        <MenuItem value="">Sin permiso</MenuItem>
                        {
                          access_obj.list.map((acc, index_access) => (
                            <MenuItem key={index_access} value={acc.value}>{acc.label}</MenuItem>
                          ))
                        }
                      </Select>
                    </FormControl>
                  </Grid>
                );
              }
            })
          }
        </Grid>
        {
          isError &&
          <Alert severity="error">
            {message}
          </Alert>
        }
        <Grid container justifyContent="flex-end">
          <Grid item>
            <Button
              type="button"
              variant="contained"
              className={classes.button}
              onClick={handleGoBack}
              disabled={isLoading}
            >
              Volver
            </Button>
          </Grid>
          {
            !readOnly &&
            <Grid item>
              <NeokodeButton
                type="submit"
                variant="contained"
                className={classes.button}
                disabled={isLoading}
              >
                {
                  isLoading ?
                  <CircularProgress />
                  :
                  (
                    (props.user || lookupMode === 'member') ? 'Editar Usuario'
                    : lookupMode === 'invite' ? 'Agregar a esta empresa'
                    : 'Agregar Usuario'
                  )
                }
              </NeokodeButton>
            </Grid>
          }
        </Grid>
      </form>
    </>
  );
}

export default UserForm;